// Credits Repository - handles all credits data access
import { supabase } from '@/integrations/supabase/client';
import { UserCredits, GUEST_CREDITS_KEY, DEFAULT_GUEST_CREDITS, DEFAULT_USER_CREDITS } from '@/models/credits';

export const creditsRepository = {
  // Get credits for a user
  async getUserCredits(userId: string): Promise<UserCredits | null> {
    const { data, error } = await supabase
      .from('user_credits')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();

    if (error) {
      console.error('Error loading credits:', error);
      return null;
    }

    return data ? {
      id: data.id,
      user_id: data.user_id,
      credits_remaining: data.credits_remaining,
      credits_used: data.credits_used,
      token_budget: data.token_budget,
      tokens_used: data.tokens_used,
      created_at: data.created_at || undefined,
      updated_at: data.updated_at || undefined
    } : null;
  },

  // Create initial credits for a user
  async createUserCredits(userId: string, initialCredits: number): Promise<UserCredits | null> {
    const { data, error } = await supabase
      .from('user_credits')
      .insert({
        user_id: userId,
        credits_remaining: initialCredits,
        credits_used: 0
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating credits:', error);
      return null;
    }

    return data ? {
      id: data.id,
      user_id: data.user_id,
      credits_remaining: data.credits_remaining,
      credits_used: data.credits_used
    } : null;
  },

  // Use a credit atomically (prevents race conditions)
  async useCredit(userId: string): Promise<{ success: boolean; newRemaining: number; newUsed: number }> {
    const { data, error } = await supabase.rpc('use_credit', { p_user_id: userId });

    if (error) {
      console.error('Error using credit:', error);
      return { success: false, newRemaining: 0, newUsed: 0 };
    }

    if (data && data.length > 0) {
      return {
        success: data[0].success,
        newRemaining: data[0].new_remaining,
        newUsed: data[0].new_used
      };
    }

    return { success: false, newRemaining: 0, newUsed: 0 };
  },

  // Guest credits operations (localStorage)
  getGuestCreditsUsed(): number {
    return parseInt(localStorage.getItem(GUEST_CREDITS_KEY) || '0', 10);
  },

  setGuestCreditsUsed(used: number): void {
    localStorage.setItem(GUEST_CREDITS_KEY, used.toString());
  },

  useGuestCredit(): number {
    const currentUsed = this.getGuestCreditsUsed();
    const newUsed = currentUsed + 1;
    this.setGuestCreditsUsed(newUsed);
    return newUsed;
  },

  refundGuestCredit(): number {
    const newUsed = Math.max(0, this.getGuestCreditsUsed() - 1);
    this.setGuestCreditsUsed(newUsed);
    return newUsed;
  },

  hasGuestRefund(chatId: string): boolean {
    try { return localStorage.getItem(`guest_credit_refunded_${chatId}`) === '1'; } catch { return false; }
  },

  markGuestRefund(chatId: string): void {
    try { localStorage.setItem(`guest_credit_refunded_${chatId}`, '1'); } catch { /* storage unavailable */ }
  },

  // Get configurable credit amounts from feature flags
  async getGuestCreditsAmount(): Promise<number> {
    const { data } = await supabase
      .from('feature_flags')
      .select('numeric_value')
      .eq('key', 'guest_credits_amount')
      .eq('enabled', true)
      .maybeSingle();

    return data?.numeric_value || DEFAULT_GUEST_CREDITS;
  },

  async getFreeCreditsAmount(): Promise<number> {
    const { data } = await supabase
      .from('feature_flags')
      .select('numeric_value')
      .eq('key', 'free_credits_amount')
      .eq('enabled', true)
      .maybeSingle();

    return data?.numeric_value || DEFAULT_USER_CREDITS;
  },

  // Get tokens per credit conversion rate
  async getTokensPerCredit(): Promise<number> {
    const { data } = await supabase
      .from('feature_flags')
      .select('numeric_value')
      .eq('key', 'tokens_per_credit')
      .eq('enabled', true)
      .maybeSingle();

    return data?.numeric_value || 100000; // Default: 100K tokens = 1 credit
  },

  // Use tokens atomically via RPC function (prevents race conditions)
  async useTokensAndDeductCredits(
    userId: string,
    tokensUsed: number,
    chatId?: string,
    modelId?: string,
    promptTokens?: number,
    completionTokens?: number,
    estimatedCost?: number,
    requestedModelId?: string
  ): Promise<{ success: boolean; creditsDeducted: number; newCreditsRemaining: number }> {
    // Ensure modelId is never undefined/null - use 'unknown' as fallback
    const actualModelId = modelId || 'unknown';
    console.log('[creditsRepository.useTokensAndDeductCredits] userId:', userId, 'chatId:', chatId, 'modelId:', actualModelId, 'requestedModelId:', requestedModelId, 'tokens:', tokensUsed, 'cost:', estimatedCost);
    
    // Use actual token split if provided, otherwise approximate
    const actualPromptTokens = promptTokens ?? Math.floor(tokensUsed / 2);
    const actualCompletionTokens = completionTokens ?? Math.ceil(tokensUsed / 2);
    const actualCost = estimatedCost ?? 0;
    
    // Use atomic RPC function to track tokens (with requested model for fallback tracking)
    const { data, error } = await supabase.rpc('use_tokens', {
      p_user_id: userId,
      p_chat_id: chatId || null,
      p_model_id: actualModelId,
      p_prompt_tokens: actualPromptTokens,
      p_completion_tokens: actualCompletionTokens,
      p_estimated_cost: actualCost,
      p_requested_model_id: requestedModelId || null
    });

    if (error) {
      console.error('Error using tokens:', error);
      return { success: false, creditsDeducted: 0, newCreditsRemaining: 0 };
    }

    if (data && data.length > 0) {
      return {
        success: data[0].success,
        creditsDeducted: 0, // Token-based system doesn't deduct credits per-call
        newCreditsRemaining: data[0].new_budget_remaining
      };
    }

    return { success: false, creditsDeducted: 0, newCreditsRemaining: 0 };
  }
};
